import { MinMaxData } from '../types'
import { Volume, VolumeEntry } from 'pages/app/consumption/your-consumption/components/consumption-section/types.ts'
import { Granularity } from 'types/contracts.ts'
import { isIntradayGranularity } from 'utils/date.ts'
import { ENERGY_TYPES, EnergyType } from '@boltenergy-be/shared'
import { DSChartColors } from 'types/colors.ts'

/**
 * Helper function to calculate the sum for a given Volume
 *
 * @param {Volume} type
 * @returns {number}
 */
export const sumVolume = (type?: Volume): number => {
  return (type?.billed || 0) + (type?.unbilled || 0)
}

/**
 * calculate min and max consumption & history data for Y axis
 * @param {VolumeEntry[]} data
 * @param {boolean} isProducer
 * @returns {MinMaxData}
 */
export const calculateMinMaxConsumptionData = (data: VolumeEntry[], isProducer: boolean): MinMaxData => {
  let max = 0
  let min = 0

  data.forEach(({ consumption, injection }) => {
    max = Math.max(sumVolume(isProducer ? injection : consumption), max)

    // Only calculate min if there is electricity injection data
    if (injection && !isProducer) {
      min = Math.min(sumVolume(injection), min)
    }
  })

  return {
    maxDataValue: max,
    minDataValue: min
  }
}

/**
 * Returns the amount of fraction digits to show for a single volume: a period within a day often only holds a fraction
 * of a kWh, while a day, month or total is always shown as a whole kWh
 *
 * @param {Granularity} granularity
 * @returns {number}
 */
export const getVolumeFractionDigits = (granularity: Granularity): number => (isIntradayGranularity(granularity) ? 3 : 0)

/**
 * Get the color for a consumption entry based on its type and variant (billed or unbilled)
 * @param {EnergyType | "IN"} type
 * @param {"billed" | "unbilled"} variant
 * @param {boolean} [hover]
 * @returns {DSChartColors}
 */
export const getConsumptionEntryColor = (type: EnergyType | 'IN', variant: 'billed' | 'unbilled', hover?: boolean): DSChartColors => {
  if (hover) {
    switch (type) {
      case ENERGY_TYPES.electricity:
        return variant === 'billed' ? DSChartColors.GREEN_HOVER : DSChartColors.GREEN_SUBDUED_HOVER
      case ENERGY_TYPES.gas:
        return variant === 'billed' ? DSChartColors.PURPLE_HOVER : DSChartColors.PURPLE_SUBDUED_HOVER
      case 'IN':
        return variant === 'billed' ? DSChartColors.YELLOW_HOVER : DSChartColors.YELLOW_SUBDUED_HOVER
    }
  }

  switch (type) {
    case ENERGY_TYPES.electricity:
      return variant === 'billed' ? DSChartColors.GREEN : DSChartColors.GREEN_SUBDUED
    case ENERGY_TYPES.gas:
      return variant === 'billed' ? DSChartColors.PURPLE : DSChartColors.PURPLE_SUBDUED
    case 'IN':
      return variant === 'billed' ? DSChartColors.YELLOW : DSChartColors.YELLOW_SUBDUED
  }
}
